/**
 * <ActivityWork> — ce que l'élève a rendu (organism).
 *
 * Une activité écrite rend des photos de la copie ; une activité orale rend un enregistrement.
 * Quand une correction existe, elle est la PREMIÈRE image — c'est l'ordre du legacy, et c'est ce
 * qui fait titrer « Rendu + correction ».
 *
 * La correction se lit de deux façons, selon ce qu'elle porte :
 *  - chaque point a son ancre → `<ActivityCorrectedCopy>` : la copie brute en grand, des pastilles
 *    cliquables posées dessus, le point choisi en dessous (le modèle tuteur de la TodoModal) ;
 *  - sinon (une correction ancienne) → l'image cuite en tête des photos, et sous elles la liste des
 *    points (`<ActivityCorrectionPoints>`).
 * Le legacy réservait les points au tuteur : le parent ne lisait que des numéros.
 *
 * Deux options pour le centre du live du masteur, au défaut neutre (la fiche du parent et le live de
 * l'élève ne changent pas) :
 *  - `stage` : sans titre, les commandes de la copie posées sur la page (`<ActivityCorrectedCopy
 *    controls="overlay">`) — la copie prend toute la place ;
 *  - `hideCorrection` : la copie nue, ni correction ni pastilles — pendant une présentation, les élèves
 *    voient l'écran du masteur (l'ancienne `TodoModal` la masquait aussi).
 *
 * L'enregistrement d'une activité orale : sans `audio`, la ligne dit qu'il existe (la fiche du
 * parent). Avec `audio` (l'élève relit son activité), la ligne le joue : le bouton lire / pause et
 * l'avancée, le lecteur étant un effet de l'écran, comme pour un vocal de la messagerie.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { formatMinutesSeconds } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UIActivityWork } from "../../../ports/primary/ui/types"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { ActivityCorrectedCopy } from "../ActivityCorrectedCopy"
import { ActivityCorrectionPoints } from "../ActivityCorrectionPoints"
import { ActivityMediaList } from "../ActivityMediaList"
import { activityWorkShows, bareWorkImages } from "./activityWorkContent"

export type ActivityWorkProps = {
    work: UIActivityWork
    /**
     * Remplir la hauteur donnée par le parent : la copie à pastilles tient alors entière, son point
     * à côté (`<ActivityCorrectedCopy fill>`). Sans effet sur le reste. Défaut `false`.
     */
    fill?: boolean
    /** L'enregistrement se joue : sa lecture (contrôlée). Absent : la ligne dit seulement qu'il existe. */
    audio?: ActivityWorkAudio
    /** Le centre du live : sans titre, les commandes sur la page. Défaut `false`. */
    stage?: boolean
    /** La copie nue, sans correction (une présentation en cours). Défaut `false`. */
    hideCorrection?: boolean
    testID?: string
}

/** La lecture de l'enregistrement : l'écran tient le lecteur, la ligne le montre. */
export type ActivityWorkAudio = {
    playing: boolean
    /** L'avancée, de 0 à 1. */
    progress: number
    onTogglePlay: () => void
}

const TEXTS = {
    recording: "Enregistrement",
    play: "Écouter l'enregistrement",
    pause: "Mettre en pause",
}

export function ActivityWork(props: ActivityWorkProps) {
    const { work, fill = false, audio, stage = false, hideCorrection = false, testID } = props

    const images = hideCorrection ? bareWorkImages(work) : work.images
    if (!activityWorkShows(work, hideCorrection)) return null
    const hasImages = images.length > 0
    const hasAudio = work.audioUri != null && work.audioUri !== ""

    const label = work.hasCorrection === true && !hideCorrection ? "Rendu + correction" : "Rendu"

    return (
        <Stack gap="3" style={fill ? styles.fill : undefined} testID={testID}>
            {!stage && <SectionLabel>{label}</SectionLabel>}
            {work.copyPages != null && work.correctionPoints != null ? (
                // Clé : une autre copie repart sur son premier point, pas sur la sélection d'avant. Nue
                // (`hideCorrection`), la même page en grand, sans pastilles ni point.
                <ActivityCorrectedCopy
                    key={work.copyPages.join("|")}
                    pages={work.copyPages}
                    points={work.correctionPoints}
                    fill={fill}
                    bare={hideCorrection}
                    controls={stage ? "overlay" : "rows"}
                />
            ) : (
                <>
                    {hasImages && <ActivityMediaList media={images.map((uri) => ({ kind: "image" as const, uri }))} />}
                    {!hideCorrection && work.correctionPoints != null && (
                        <ActivityCorrectionPoints points={work.correctionPoints} />
                    )}
                </>
            )}
            {hasAudio && (
                <Card p="3">
                    <Row gap="3" align="center">
                        {audio != null ? (
                            <IconButton
                                iconName={audio.playing ? "pause" : "play"}
                                size={44}
                                iconSize={18}
                                onPress={audio.onTogglePlay}
                                accessibilityLabel={audio.playing ? TEXTS.pause : TEXTS.play}
                            />
                        ) : (
                            <IconBubble iconName="headset" />
                        )}
                        <Stack gap={audio != null ? "1" : "0"} style={audio != null ? styles.grow : undefined}>
                            <Text weight="semibold">{TEXTS.recording}</Text>
                            {audio != null && <ProgressBar value={audio.progress * 100} height={4} />}
                            {work.audioDurationSeconds != null && (
                                <Text size="text-sm" color="text-muted">
                                    {formatMinutesSeconds(work.audioDurationSeconds)}
                                </Text>
                            )}
                        </Stack>
                    </Row>
                </Card>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    fill: { flex: 1, minHeight: 0 },
    grow: { flex: 1, minWidth: 0 },
}))
